/**
 * Where a signed-out user goes.
 *
 * Both frontends authenticate against the same backend identity — one cookie, one
 * `isSessionActive` flag, one `currentToken`. So a logout, an idle timeout, or an
 * expired/replaced token in EITHER app has to end at the DCC (adib-frontend) login
 * screen. The compliance app's own /login is only reachable by typing it directly,
 * for when this app is run standalone.
 *
 * The origin comes from ./dccApp (REACT_APP_URL_DCC). It used to be read inline at six
 * call sites as `window.location.href = process.env.REACT_APP_URL_DCC || ''` — and it was
 * missing from .env, so every one of those evaluated to `href = ''`, which re-loads the
 * CURRENT page instead of leaving it. That is why "Logout" looked like it did nothing.
 */
import { dccUrl } from './dccApp';

export const DCC_LOGIN_URL = dccUrl('/login');

export interface DccLoginOptions {
  /**
   * What to send as ?next=, so the DCC login can return the user where they were.
   *
   *   true (default) — the current URL. Right for every INVOLUNTARY exit: an expired
   *                    token, an idle timeout, a session claimed elsewhere.
   *   false          — send nothing. For a deliberate Logout: someone who chose to leave
   *                    should land on a clean login and then the DCC home, not be thrown
   *                    back into the screen they just left.
   *   a string       — an explicit URL. Used by <NotAuthorized />, where the address bar
   *                    may no longer hold the page the user actually wanted (a mid-session
   *                    401 can arrive after they have navigated on); it passes the URL
   *                    captured at the moment the session died. See utils/attemptedUrl.
   */
  returnTo?: boolean | string;
}

/** Paths that must never become a ?next= target — returning to them just re-runs the exit. */
const NON_RETURNABLE = ['/login', '/register', '/logout-clear'];

/**
 * Leave for the DCC login screen.
 *
 * Uses location.replace() rather than assigning href: the page we are leaving is dead
 * (no session), so it must not stay in history where Back would land on it and bounce
 * the user straight back here.
 *
 * The ?next= value is an ABSOLUTE url because the login lives on a different origin
 * (:3001 → :5050), so a bare path would resolve against the DCC instead of this app. The
 * DCC validates it against an allowlist before honouring it — see adib-frontend2's
 * utils/postLoginRedirect — because a login that forwards to any caller-supplied address
 * is an open redirect.
 *
 * @param reason  optional short slug appended as ?reason= for the login screen to show.
 * @param options see DccLoginOptions.
 */
export function goToDccLogin(reason?: string, options: DccLoginOptions = {}): void {
  if (typeof window === 'undefined') return;
  const { returnTo = true } = options;

  const params = new URLSearchParams();
  if (reason) params.set('reason', reason);

  if (returnTo !== false) {
    const candidate = typeof returnTo === 'string' ? returnTo : window.location.href;
    // Resolve before testing the path: an explicit candidate may be relative, and a bare
    // string comparison would miss '/login' hiding inside a full URL.
    let pathname = '';
    try {
      pathname = new URL(candidate, window.location.origin).pathname;
    } catch {
      pathname = '';
    }
    if (pathname && !NON_RETURNABLE.includes(pathname)) {
      params.set('next', new URL(candidate, window.location.origin).toString());
    }
  }

  const query = params.toString();
  window.location.replace(query ? `${DCC_LOGIN_URL}?${query}` : DCC_LOGIN_URL);
}

export default goToDccLogin;
